// =============================================================================
// Settings UI — forwarding destination and system health, with archived test tools
// =============================================================================
// Tester selects a curated profile + language, sees the credentials, then
// starts a call. The PRODUCTION agent flow runs identically (spell-back,
// fuzzy matching, retries, GDPR step). MCP reads return pre-cached profile
// data; MCP writes (createCase) return mock CAS-TEST-XXXXXXX numbers.
//
// Visible sub-tabs: Destination number · System-Health. Other tools remain hidden.
// =============================================================================

import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { stateful, useEffect, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { CallLog, subscribeLogsForCallID } from '@adornis/voice-assistant/call-logs.js';
import type { BrowserVoiceCallState } from '@adornis/voice-assistant/client/browser-call.js';
import { BrowserVoiceCall } from '@adornis/voice-assistant/client/browser-call.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { html, nothing } from 'lit';
import { of } from 'rxjs';
import arrowLeftIcon from '../assets/arrow_left.svg';
import callIcon from '../assets/call.svg';
import { CallSession } from '../../db/CallSession.js';
import {
  getPricingConfig,
  savePricingConfig,
  systemHealth,
  testLabGetBoard,
  testLabHistory,
  testLabListCallers,
  testLabListScenarios,
  testLabRequestTestSession,
  testLabSaveBoard,
  testLabSubscribeVerdicts,
  zwJudgeCall,
  zwJudgeList,
  zwJudgeRecent,
  zwJudgeTranscript,
} from '../../db/test-lab-resolvers.js';
import { callLogEventTypes, renderCallLogList } from './CallLogList.js';
import { renderCallTranscript, spokenText } from './CallTranscript.js';
import { CallFeedbackPanel } from './CallFeedbackPanel.js';
import { CallForwardingPanel } from './CallForwardingPanel.js';
import { TelephoneFlowPanel } from './TelephoneFlowPanel.js';
import { CostAnalytics } from './CostAnalytics.js';
import { AutomatedSuitePage } from './AutomatedSuitePage.js';

// ─── Types mirror server's TestCallerProfile shape (over the wire as JSON) ──
interface TestCallerCredentialsUI {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  accountNumber: string;
  dossierNumbers: string[];
  ticketNumber?: string;
}
interface TestCallerProfileUI {
  profileId: string;
  label: string;
  description: string;
  expectedFlow: string;
  credentials: TestCallerCredentialsUI;
}

interface TestScenarioUI {
  id: string;
  label: string;
  description: string;
}

interface VerdictUI {
  check: string;
  passed: boolean;
  kind: string;
  reason?: string;
  ts: number;
  final: boolean;
}

type TabId = 'destination' | 'telephone-flow' | 'tests' | 'kosten' | 'health' | 'board' | 'judge' | 'ndi-voice';

// 🆕 2026-08-29 — the tab bar in display order, and the ones hidden on request.
//
// Hiding, not removing: every tab still renders if its id is taken out of HIDDEN_TABS, so this is
// one edit to undo. Keeping the order in a single array is what lets the default tab be derived
// rather than hardcoded — hiding the tab that used to be the default would otherwise open the
// page on a tab with no button to leave it.
const TAB_ORDER: Array<[TabId, string]> = [
  ['destination', 'Destination number'],
  ['telephone-flow', 'Gesprächsablauf'],
  ['tests', 'Tests'],
  ['kosten', 'Kosten'],
  ['health', 'System-Health'],
  ['board', 'Quick Board'],
  ['judge', 'Call Judge'],
  ['ndi-voice', 'NDI Voice-Testing'],
];
const HIDDEN_TABS: TabId[] = ['tests', 'kosten', 'ndi-voice', 'board', 'judge'];
const tabVisible = (id: TabId) => !HIDDEN_TABS.includes(id);
const VISIBLE_TABS = TAB_ORDER.filter(([id]) => tabVisible(id));
const DEFAULT_TAB: TabId = VISIBLE_TABS[0]?.[0] ?? 'health';

const verdictBadge = (v: VerdictUI) => {
  const cls = v.passed
    ? 'badge-success'
    : v.kind === 'ordering-gate'
      ? 'badge-warning'
      : v.kind === 'validation'
        ? 'badge-info'
        : 'badge-error';
  return html`<span class="badge ${cls}">${v.passed ? 'PASS' : v.kind.toUpperCase()}</span>`;
};

const flowBadge = (flow: string) => {
  const map: Record<string, string> = {
    'path-a': 'badge-info',
    'path-b': 'badge-primary',
    'path-c-21': 'badge-secondary',
    'path-c-22': 'badge-accent',
    'existing-ticket': 'badge-warning',
  };
  const cls = map[flow] ?? 'badge-ghost';
  return html`<span class="badge ${cls} font-mono">${flow}</span>`;
};

export const TestLabPage = stateful(() => {
  const [activeTab, setActiveTab] = useState<TabId>(DEFAULT_TAB);
  const [callState, setCallState] = useState<BrowserVoiceCallState>('idle');
  const [call, setCall] = useState<BrowserVoiceCall | null>(null);
  const [activeSessionID, setActiveSessionID] = useState<string | null>(null);
  const [verdicts, setVerdicts] = useState<VerdictUI[]>([]);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [selectedProfileId, setSelectedProfileId] = useState<string>('');
  const [selectedScenario, setSelectedScenario] = useState<string>('full-flow');
  const [selectedLang, setSelectedLang] = useState<'de' | 'en'>('de');
  const [logTypeFilter, setLogTypeFilter] = useState<string>('all');
  // System health panel state.
  const [healthData, setHealthData] = useState<any | null>(null);
  const [healthLoading, setHealthLoading] = useState<boolean>(false);
  const [healthError, setHealthError] = useState<string | null>(null);

  // Call-Judge panel state. `judgeBusy` holds the callID currently being judged so a single
  // row can show a spinner without freezing the whole table.
  const [judgeRows, setJudgeRows] = useState<any[]>([]);
  const [judgeRules, setJudgeRules] = useState<any[]>([]);
  const [judgeLoaded, setJudgeLoaded] = useState<boolean>(false);
  const [judgeLoading, setJudgeLoading] = useState<boolean>(false);
  const [judgeBusy, setJudgeBusy] = useState<string | null>(null);
  const [judgeError, setJudgeError] = useState<string | null>(null);
  const [judgeOpen, setJudgeOpen] = useState<string | null>(null);
  const [judgeCallInput, setJudgeCallInput] = useState<string>('');
  // Transcripts are fetched when a verdict is expanded and kept per call, so collapsing and
  // reopening a row does not re-query. They are never fetched for rows nobody opened.
  const [judgeTurns, setJudgeTurns] = useState<Record<string, any[]>>({});
  const [judgeTurnsBusy, setJudgeTurnsBusy] = useState<string | null>(null);
  const [judgeLinkBase, setJudgeLinkBase] = useState<string>('');
  // Quick Board (shared links + SOS tasks) state.
  const [boardLinks, setBoardLinks] = useState<Array<{ title: string; url: string }>>([]);
  const [boardTasks, setBoardTasks] = useState<Array<{ id: string; text: string; done: boolean }>>([]);
  const [boardLoaded, setBoardLoaded] = useState<boolean>(false);
  const [boardSaving, setBoardSaving] = useState<boolean>(false);
  const [newLinkTitle, setNewLinkTitle] = useState<string>('');
  const [newLinkUrl, setNewLinkUrl] = useState<string>('');
  const [newTaskText, setNewTaskText] = useState<string>('');
  // ── Pricing config (UI-editable rates) state ──
  const PRICING_DEFAULT: Record<string, number> = {
    orIn: 2.0,
    orOut: 6.0,
    azIn: 2.0,
    azOut: 6.0,
    cartesia: 65.0,
    deepgram: 0.0077,
    twilio: 0.014,
    usdToEur: 0.92,
  };
  const [pricing, setPricing] = useState<Record<string, number>>({ ...PRICING_DEFAULT });
  const [pricingLoaded, setPricingLoaded] = useState<boolean>(false);
  const [pricingSaving, setPricingSaving] = useState<boolean>(false);
  const [pricingSaved, setPricingSaved] = useState<boolean>(false);
  const [pricingError, setPricingError] = useState<string | null>(null);
  const [pricingMeta, setPricingMeta] = useState<{ by: string; at: string | null } | null>(null);

  // Load caller profiles (admin-gated). useQuery accepts Promise directly.
  const callersQuery = useQuery(() => testLabListCallers()(), []);
  const scenariosQuery = useQuery(() => testLabListScenarios()(), []);
  const boardQuery = useQuery(() => testLabGetBoard()(), []);
  const pricingQuery = useQuery(() => getPricingConfig()(), []);

  const callers: TestCallerProfileUI[] = (() => {
    try {
      const raw = (callersQuery.value as any) ?? '';
      if (typeof raw === 'string' && raw.length > 0) return JSON.parse(raw);
      return [];
    } catch {
      return [];
    }
  })();

  const scenarios: TestScenarioUI[] = (() => {
    try {
      const raw = (scenariosQuery.value as any) ?? '';
      if (typeof raw === 'string' && raw.length > 0) return JSON.parse(raw);
      return [];
    } catch {
      return [];
    }
  })();

  const selectedScenarioInfo = scenarios.find(s => s.id === selectedScenario);

  // ── Quick Board: load once + edit helpers (persist to shared backend) ────
  useEffect(() => {
    if (!boardLoaded && typeof boardQuery.value === 'string' && boardQuery.value.length > 0) {
      try {
        const d = JSON.parse(boardQuery.value as string);
        setBoardLinks(Array.isArray(d.links) ? d.links : []);
        setBoardTasks(Array.isArray(d.tasks) ? d.tasks : []);
      } catch {
        /* ignore malformed board */
      }
      setBoardLoaded(true);
    }
  }, [boardQuery.value, boardLoaded]);

  const persistBoard = async (
    links: Array<{ title: string; url: string }>,
    tasks: Array<{ id: string; text: string; done: boolean }>,
  ) => {
    setBoardSaving(true);
    try {
      await testLabSaveBoard(JSON.stringify({ links, tasks }))();
    } catch (err) {
      console.error('Quick Board save failed:', err);
    } finally {
      setBoardSaving(false);
    }
  };

  const sanitizeUrl = (u: string): string | null => {
    let s = (u ?? '').trim();
    if (!s) return null;
    if (!/^https?:\/\//i.test(s)) s = 'https://' + s;
    try {
      const parsed = new URL(s);
      if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null;
      return s;
    } catch {
      return null;
    }
  };

  const addLink = () => {
    const url = sanitizeUrl(newLinkUrl);
    if (!url) return;
    const next = [...boardLinks, { title: newLinkTitle.trim() || url, url }];
    setBoardLinks(next);
    setNewLinkTitle('');
    setNewLinkUrl('');
    persistBoard(next, boardTasks);
  };
  const removeLink = (idx: number) => {
    const next = boardLinks.filter((_, i) => i !== idx);
    setBoardLinks(next);
    persistBoard(next, boardTasks);
  };
  const addTask = () => {
    const text = newTaskText.trim();
    if (!text) return;
    const id = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : String(Date.now());
    const next = [...boardTasks, { id, text, done: false }];
    setBoardTasks(next);
    setNewTaskText('');
    persistBoard(boardLinks, next);
  };
  const toggleTask = (id: string) => {
    const next = boardTasks.map(t => (t.id === id ? { ...t, done: !t.done } : t));
    setBoardTasks(next);
    persistBoard(boardLinks, next);
  };
  const removeTask = (id: string) => {
    const next = boardTasks.filter(t => t.id !== id);
    setBoardTasks(next);
    persistBoard(boardLinks, next);
  };

  // ── Pricing config: load once + save ──
  useEffect(() => {
    if (!pricingLoaded && typeof pricingQuery.value === 'string' && pricingQuery.value.length > 0) {
      try {
        const w = JSON.parse(pricingQuery.value as string);
        const r = w.rates ?? w;
        setPricing({
          orIn: r?.llm?.openrouter?.inPerM ?? PRICING_DEFAULT.orIn,
          orOut: r?.llm?.openrouter?.outPerM ?? PRICING_DEFAULT.orOut,
          azIn: r?.llm?.azure?.inPerM ?? PRICING_DEFAULT.azIn,
          azOut: r?.llm?.azure?.outPerM ?? PRICING_DEFAULT.azOut,
          cartesia: r?.cartesia?.usdPer1MChars ?? PRICING_DEFAULT.cartesia,
          deepgram: r?.deepgram?.usdPerMin ?? PRICING_DEFAULT.deepgram,
          twilio: r?.twilio?.usdPerMin ?? PRICING_DEFAULT.twilio,
          usdToEur: r?.usdToEur ?? PRICING_DEFAULT.usdToEur,
        });
        if (w.updatedBy) setPricingMeta({ by: w.updatedBy, at: w.updatedAt ?? null });
      } catch {
        /* ignore malformed */
      }
      setPricingLoaded(true);
    }
  }, [pricingQuery.value, pricingLoaded]);

  // NB: functional-lit useState does NOT support the (prev)=>next updater form —
  // pass a direct object built from the current `pricing` closure (matches the
  // rest of this file, e.g. toggleTask). The updater form stored the function
  // itself, making every pricing[k] undefined → all fields showed 0.
  const setPrice = (k: string, v: string) => setPricing({ ...pricing, [k]: v === '' ? 0 : Number(v) });
  const eurPerMin = () => {
    const fx = pricing.usdToEur || 0.92;
    const llm = ((900 * pricing.azIn + 250 * pricing.azOut) / 1_000_000) * fx;
    const cartesia = ((820 * pricing.cartesia) / 1_000_000) * fx;
    const deepgram = pricing.deepgram * fx;
    const twilio = pricing.twilio * fx;
    return { llm, cartesia, deepgram, twilio, total: llm + cartesia + deepgram + twilio };
  };
  const savePricing = async () => {
    setPricingSaving(true);
    setPricingError(null);
    setPricingSaved(false);
    const payload = {
      llm: {
        openrouter: { inPerM: pricing.orIn, outPerM: pricing.orOut },
        azure: { inPerM: pricing.azIn, outPerM: pricing.azOut },
      },
      cartesia: { usdPer1MChars: pricing.cartesia },
      deepgram: { usdPerMin: pricing.deepgram },
      twilio: { usdPerMin: pricing.twilio },
      usdToEur: pricing.usdToEur,
    };
    try {
      await savePricingConfig(JSON.stringify(payload))();
      setPricingSaved(true);
      setTimeout(() => setPricingSaved(false), 2500);
    } catch (err) {
      setPricingError(err instanceof Error ? err.message : String(err));
    } finally {
      setPricingSaving(false);
    }
  };

  // Default to first profile once data loads — run AFTER render, not during.
  useEffect(() => {
    if (callers.length > 0 && !selectedProfileId) {
      setSelectedProfileId(callers[0]!.profileId);
    }
  }, [callers.length, selectedProfileId]);

  const selectedProfile = callers.find(c => c.profileId === selectedProfileId);

  // History (last 20 test calls) — also a Promise-returning query.
  const historyQuery = useQuery(() => testLabHistory('', 20)(selectionSet(() => CallSession, 1)), [callState]);

  const sessionsList = (historyQuery.value as CallSession[] | undefined) ?? [];
  // Cost summary of the call that just finished (matched by sessionID).
  const lastCallSession = activeSessionID ? sessionsList.find(s => s.sessionID === activeSessionID) : undefined;

  // Live verdict subscription
  const verdictSub = useQuery(
    () => (activeSessionID ? testLabSubscribeVerdicts(activeSessionID)() : of(null)),
    [activeSessionID],
  );

  // Append verdicts from live subscription — AFTER render, not during.
  useEffect(() => {
    if (verdictSub.value && typeof verdictSub.value === 'string') {
      try {
        const v = JSON.parse(verdictSub.value as string) as VerdictUI;
        const last = verdicts[verdicts.length - 1];
        if (!last || last.ts !== v.ts) {
          setVerdicts([...verdicts, v]);
        }
      } catch {
        /* ignore malformed verdict frames */
      }
    }
  }, [verdictSub.value]);

  // Live call logs (transcript, tool calls, audio events)
  const callLogSub = useQuery(
    () => (activeSessionID ? subscribeLogsForCallID(activeSessionID)(selectionSet(() => CallLog, 1)) : of([])),
    [activeSessionID],
  );

  const startCall = async () => {
    if (!selectedProfileId) {
      setErrorMsg('Bitte wähle zuerst einen Test-Anrufer aus.');
      return;
    }
    if (callState !== 'idle' && callState !== 'stopped' && callState !== 'error') return;

    setErrorMsg(null);
    setVerdicts([]);
    try {
      const token = (await testLabRequestTestSession(
        selectedProfileId,
        selectedScenario,
        selectedLang,
      )()) as unknown as string;
      if (!token || typeof token !== 'string' || !token.startsWith('tlt-')) {
        throw new Error(`Invalid token returned from server: ${String(token)}`);
      }

      const newCall = new BrowserVoiceCall({
        url: '/api/browser-call/ws',
        meta: { testToken: token },
        onStateChange: state => setCallState(state),
      });
      setCall(newCall);
      const sessionID = await newCall.start();
      setActiveSessionID(sessionID);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setErrorMsg(msg);
      setCallState('error');
    }
  };

  const stopCall = async () => {
    if (call && callState === 'connected') {
      await call.stop();
    }
    setCall(null);
  };

  const handleCallButtonClick = () => {
    if (callState === 'connected') stopCall();
    else startCall();
  };

  const runHealthCheck = async (force = false) => {
    setHealthLoading(true);
    setHealthError(null);
    try {
      const raw = await systemHealth(force)();
      setHealthData(JSON.parse(raw));
    } catch (err) {
      setHealthError(err instanceof Error ? err.message : String(err));
    } finally {
      setHealthLoading(false);
    }
  };

  const callButtonLabel = (() => {
    switch (callState) {
      case 'connecting':
        return 'Verbinde…';
      case 'connected':
        return 'Anruf beenden';
      case 'error':
        return 'Erneut versuchen';
      default:
        return 'Test-Anruf starten';
    }
  })();

  const callButtonClass = (() => {
    const base = 'btn shadow-lg';
    if (callState === 'connecting') return `${base} btn-warning`;
    if (callState === 'connected') return `${base} btn-error`;
    return `${base} btn-primary`;
  })();

  const formatDate = (d: Date | string | null | undefined): string => {
    if (!d) return '—';
    try {
      return new Date(d).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'medium' });
    } catch {
      return '—';
    }
  };

  const eur = (n: number | null | undefined) => (typeof n === 'number' ? `€${n.toFixed(4)}` : '—');
  const usd = (n: number | null | undefined) => (typeof n === 'number' ? `$${n.toFixed(4)}` : '—');
  // 🆕 cost-accuracy badge driven by estimationMethod.
  const costMethodBadge = (m: string | null | undefined) => {
    const s = m ?? '';
    if (s.startsWith('provider-exact')) return html`<span class="badge badge-success badge-sm">Exakt</span>`;
    if (s.startsWith('retokenized')) return html`<span class="badge badge-info badge-sm">Prognose+</span>`;
    return html`<span class="badge badge-ghost badge-sm">Prognose</span>`;
  };

  // ════════════════════════════════════════════════════════════════════════
  // Call Judge — post-hoc audit of finished calls
  // ════════════════════════════════════════════════════════════════════════
  // The judge is given two things: (a) the five documented conversation paths and (b) the full
  // log of a FINISHED call. It marks hallucinations and violations of the instructions the agent
  // received in that very turn.
  //
  // Three deliberate decisions:
  //   1. Manual trigger. A judge that has not been calibrated is a noise generator. Point it at
  //      calls whose verdict we already know before letting it speak about calls we do not.
  //   2. Every finding carries a VERBATIM quote from the log, and the quote must come from the
  //      role it is attributed to. Findings that fail that check are dropped server-side
  //      (`verifyQuotes`) — otherwise the auditor hallucinates about the hallucination.
  //   3. The path is derived in code from the tool calls, never guessed. The model is asked for
  //      it separately, and a disagreement is itself a finding.
  //
  // The panel shows the conversation, not just the verdict. A finding you cannot check against
  // the line it came from is a rumour, so each one is printed in the margin beside its own turn.
  //
  // The judge is triage, not a seal of approval: it decides nothing, it only marks where to look.

  const loadJudge = async () => {
    setJudgeLoading(true);
    setJudgeError(null);
    try {
      const raw = await zwJudgeList(50)();
      const d = JSON.parse(String(raw ?? '{}'));
      setJudgeRows(Array.isArray(d.verdicts) ? d.verdicts : []);
      setJudgeRules(Array.isArray(d.rules) ? d.rules : []);
      setJudgeLinkBase(typeof d.callLinkBase === 'string' ? d.callLinkBase : '');
      setJudgeLoaded(true);
    } catch (err) {
      setJudgeError(err instanceof Error ? err.message : String(err));
    } finally {
      setJudgeLoading(false);
    }
  };

  // `force` exists because this closure's `judgeTurns` is the value from the render that created
  // it. runJudgeOne clears the entry and calls this in the same tick, so without force the guard
  // reads the pre-clear cache, sees a hit, and returns without fetching — which is what printed
  // "No conversation stored for this call." on a call that has 44 turns.
  const loadTurns = async (callID: string, force = false) => {
    if (!force && judgeTurns[callID]) return;
    setJudgeTurnsBusy(callID);
    try {
      const raw = await zwJudgeTranscript(callID)();
      const d = JSON.parse(String(raw ?? '{}'));
      setJudgeTurns({ ...judgeTurns, [callID]: Array.isArray(d.turns) ? d.turns : [] });
    } catch (err) {
      setJudgeError(err instanceof Error ? err.message : String(err));
    } finally {
      setJudgeTurnsBusy(null);
    }
  };

  const runJudgeRecent = async () => {
    setJudgeBusy('__recent__');
    setJudgeError(null);
    try {
      await zwJudgeRecent(5)();
      await loadJudge();
    } catch (err) {
      setJudgeError(err instanceof Error ? err.message : String(err));
    } finally {
      setJudgeBusy(null);
    }
  };

  const runJudgeOne = async (callID: string) => {
    const id = (callID ?? '').trim();
    if (!id) {
      setJudgeError('Please enter a call ID.');
      return;
    }
    setJudgeBusy(id);
    setJudgeError(null);
    try {
      await zwJudgeCall(id)();
      // The verdict changed, so the cached transcript's annotations are stale — drop it and let
      // the expanded row fetch again.
      const { [id]: _drop, ...rest } = judgeTurns;
      setJudgeTurns(rest);
      await loadJudge();
      setJudgeOpen(id);
      void loadTurns(id, true);
    } catch (err) {
      setJudgeError(err instanceof Error ? err.message : String(err));
    } finally {
      setJudgeBusy(null);
    }
  };

  useEffect(() => {
    if (activeTab === 'judge' && !judgeLoaded && !judgeLoading) void loadJudge();
  }, [activeTab]);

  const sevDot = (sev: string) =>
    sev === 'critical'
      ? 'bg-error ring-2 ring-error/25'
      : sev === 'major'
        ? 'bg-error/70 ring-2 ring-error/15'
        : sev === 'minor'
          ? 'bg-warning ring-2 ring-warning/20'
          : 'bg-success ring-2 ring-success/20';

  const sevLabel = (sev: string) =>
    sev === 'critical' ? 'critical' : sev === 'major' ? 'major' : sev === 'minor' ? 'minor' : 'clean';

  const fmtWhen = (d: any) => {
    if (!d) return '—';
    const x = new Date(d);
    return Number.isNaN(x.getTime()) ? '—' : x.toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'medium' });
  };

  const fmtDur = (ms: any) => {
    const s = Math.round((Number(ms) || 0) / 1000);
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  };

  // Relative when no base is configured, so it lands on the call overview of the environment
  // serving this page — the staging build links to the staging overview without being told.
  const callLink = (callID: string) => `${judgeLinkBase}/calls/${encodeURIComponent(callID)}`;

  /** One finding, rendered as a margin note beside the line it is about. */
  const renderNote = (f: any) => html`
    <div
      class="rounded-lg border p-2.5 text-xs ${f.severity === 'critical' || f.severity === 'major'
        ? 'border-error/40 bg-error/5'
        : 'border-warning/40 bg-warning/5'}"
    >
      <div class="flex items-center gap-1.5 flex-wrap mb-1">
        <span class="inline-block w-1.5 h-1.5 rounded-full shrink-0 ${sevDot(f.severity)}"></span>
        <span class="badge badge-xs font-mono">${f.rule}</span>
        <span class="text-[10px] text-base-content/35 ml-auto">${f.source}</span>
      </div>
      <div class="text-base-content/75 leading-snug">${f.reason}</div>
      ${f.instruction
        ? html`<div class="mt-1.5 text-[11px] text-base-content/50 leading-snug">
            ${f.source === 'mechanical' ? 'measured:' : 'was told:'}
            <span class="italic">“${spokenText(f.instruction)}”</span>
          </div>`
        : nothing}
    </div>
  `;

  /** The expanded half of a verdict row: the conversation, annotated in the margin. */
  const renderVerdictBody = (v: any) => {
    const turns: any[] = judgeTurns[v.callID] ?? [];
    const findings: any[] = Array.isArray(v.findings) ? v.findings : [];

    // A finding belongs beside a turn when it names one that exists. PATH has no single line to
    // point at — it is about the call as a whole — so it goes above the transcript instead of
    // being pinned to turn 1, where it would read as an accusation about the greeting.
    const byTurn = new Map<number, any[]>();
    const callLevel: any[] = [];
    for (const f of findings) {
      const n = Number(f.turn) || 0;
      if (f.rule === 'PATH' || !turns.some(t => t.i === n)) callLevel.push(f);
      else byTurn.set(n, [...(byTurn.get(n) ?? []), f]);
    }

    return html`
      <div class="px-4 pb-4 border-t border-base-300/60 pt-3">
        <div class="flex items-center gap-2 flex-wrap mb-3">
          <a class="btn btn-xs btn-primary" href=${callLink(v.callID)} target="_blank" rel="noopener noreferrer">
            Open this call ↗
          </a>
          ${tabVisible('tests')
            ? html`<button
                class="btn btn-xs btn-outline"
                @click=${() => {
                  setActiveSessionID(v.callID);
                  setActiveTab('tests');
                }}
              >
                Raw log here
              </button>`
            : nothing}
          <button
            class="btn btn-xs btn-ghost"
            @click=${() => void runJudgeOne(v.callID)}
            ?disabled=${judgeBusy !== null}
          >
            ${judgeBusy === v.callID ? 'Judging…' : 'Judge again'}
          </button>
          <span class="text-[11px] text-base-content/40 tabular-nums ml-auto">
            call ${fmtWhen(v.callStartedAt)} · judged ${fmtWhen(v.judgedAt)} · in ${v.elapsedMs} ms
          </span>
        </div>

        ${Object.keys(v.flags ?? {}).length
          ? html`<details class="mb-3">
              <summary class="cursor-pointer select-none text-[11px] text-base-content/40 hover:text-base-content/70">
                Flags at judge time (${Object.keys(v.flags).length})
              </summary>
              <div class="flex flex-wrap gap-1 mt-2">
                ${Object.entries(v.flags).map(
                  ([k, val]) =>
                    html`<span class="badge badge-ghost badge-sm font-mono text-[10px]">${k}=${String(val)}</span>`,
                )}
              </div>
              <p class="text-[10px] text-base-content/35 mt-2">
                Recorded for reading later, not shown to the judge — a flag's effect is already in the tool results
                below, in the words the agent was given. Read at judge time, so re-judging an old call records today's
                switches.
              </p>
            </details>`
          : nothing}
        ${v.error
          ? html`<p class="text-xs text-error mb-3">
              Judge error: ${v.error} — the mechanical findings below still stand.
            </p>`
          : nothing}
        ${callLevel.length
          ? html`<div class="flex flex-col gap-2 mb-3">
              ${callLevel.map(
                (f: any) =>
                  html`<div class="rounded-lg border border-error/40 bg-error/5 p-2.5 text-xs flex items-start gap-2">
                    <span class="inline-block w-1.5 h-1.5 rounded-full shrink-0 mt-1 ${sevDot(f.severity)}"></span>
                    <span class="badge badge-xs font-mono shrink-0">${f.rule}</span>
                    <span class="text-base-content/75 leading-snug">${f.reason}</span>
                  </div>`,
              )}
            </div>`
          : nothing}
        ${findings.length === 0
          ? html`<p class="text-xs text-base-content/50 mb-3">
              No violation found. That means: nothing that breaks the rules below or the instructions given in this call
              — not that the call was good.
            </p>`
          : nothing}
        ${judgeTurnsBusy === v.callID
          ? html`<p class="text-xs text-base-content/50 flex items-center gap-2">
              <span class="loading loading-spinner loading-xs"></span>Loading the conversation…
            </p>`
          : turns.length === 0
            ? html`<p class="text-xs text-base-content/50">No conversation stored for this call.</p>`
            : renderCallTranscript(turns, {
                // The judge's findings, as margin notes beside the turn they name (shared rows: CallTranscript.ts).
                notesFor: t => (byTurn.get(t.i) ?? []).map((f: any) => renderNote(f)),
              })}
      </div>
    `;
  };

  const renderJudgeTab = () => html`
    <div class="card bg-base-100 border border-base-300 shadow-sm">
      <div class="card-body p-5">
        <div class="flex items-center gap-2.5 flex-wrap">
          <span class="font-semibold">Call Judge</span>
          <span class="text-xs text-base-content/50">
            audits finished calls against the five paths and the instructions of each individual turn
          </span>
          <span class="text-xs text-base-content/40 tabular-nums ml-auto">
            ${judgeRows.length} verdict${judgeRows.length === 1 ? '' : 's'}
          </span>
        </div>

        <div class="flex items-center gap-2 flex-wrap mt-4">
          <button
            class="btn btn-sm btn-primary gap-2"
            @click=${() => void runJudgeRecent()}
            ?disabled=${judgeBusy !== null}
          >
            ${judgeBusy === '__recent__'
              ? html`<span class="loading loading-spinner loading-xs"></span>Judging…`
              : 'Judge the 5 most recent unjudged calls'}
          </button>
          <button class="btn btn-sm btn-outline gap-2" @click=${() => void loadJudge()} ?disabled=${judgeLoading}>
            ${judgeLoading ? html`<span class="loading loading-spinner loading-xs"></span>Loading…` : 'Refresh'}
          </button>
          <div class="join ml-auto">
            <input
              class="input input-sm input-bordered join-item font-mono w-64"
              placeholder="Call ID …"
              .value=${judgeCallInput}
              @input=${(e: Event) => setJudgeCallInput((e.target as HTMLInputElement).value)}
            />
            <button
              class="btn btn-sm join-item"
              @click=${() => void runJudgeOne(judgeCallInput)}
              ?disabled=${judgeBusy !== null}
            >
              Judge this call
            </button>
          </div>
        </div>

        ${judgeError ? html`<p class="text-error text-sm mt-3">${judgeError}</p>` : nothing}
        ${judgeRows.length === 0
          ? html`<p class="text-xs text-base-content/50 mt-4">
              No verdicts yet. “Judge the 5 most recent unjudged calls” takes the newest calls from the call log that do
              not have a verdict yet. Judging one call takes about three seconds.
            </p>`
          : html`<div class="flex flex-col gap-2 mt-4">
              ${judgeRows.map(
                (v: any) =>
                  html`<div
                    class="rounded-xl border transition-colors ${v.severity === 'critical' || v.severity === 'major'
                      ? 'border-error/40 bg-error/5'
                      : v.severity === 'minor'
                        ? 'border-warning/40 bg-warning/5'
                        : 'border-base-300 bg-base-200/40'}"
                  >
                    <button
                      class="w-full text-left p-4 flex items-center gap-3 flex-wrap"
                      @click=${() => {
                        const next = judgeOpen === v.callID ? null : v.callID;
                        setJudgeOpen(next);
                        if (next) void loadTurns(next);
                      }}
                    >
                      <span class="inline-block w-2.5 h-2.5 rounded-full shrink-0 ${sevDot(v.severity)}"></span>
                      <span class="font-mono text-xs truncate max-w-[18rem]">${v.callID}</span>
                      <span class="badge badge-ghost badge-sm">${sevLabel(v.severity)}</span>
                      <span class="text-xs text-base-content/60">
                        ${v.findingCount} finding${v.findingCount === 1 ? '' : 's'}
                      </span>
                      <span class="badge badge-outline badge-sm font-mono">${v.path}</span>
                      ${v.pathJudged && v.pathJudged !== v.path && v.path !== 'unfinished'
                        ? html`<span class="badge badge-warning badge-sm">judge read: ${v.pathJudged}</span>`
                        : nothing}
                      ${v.error ? html`<span class="badge badge-error badge-sm">judge error</span>` : nothing}
                      <span class="text-[11px] text-base-content/50 tabular-nums ml-auto shrink-0">
                        ${fmtWhen(v.callStartedAt)}${v.callDurationMs ? ` · ${fmtDur(v.callDurationMs)}` : ''} ·
                        ${v.turnCount} turns
                      </span>
                    </button>
                    ${judgeOpen === v.callID ? renderVerdictBody(v) : nothing}
                  </div>`,
              )}
            </div>`}

        <details class="mt-5 border-t border-base-300 pt-3">
          <summary
            class="cursor-pointer select-none text-xs font-medium text-base-content/50 hover:text-base-content/80 transition-colors"
          >
            How does this work?
          </summary>
          <div class="text-xs text-base-content/60 leading-relaxed mt-3 space-y-2">
            <p>
              <span class="font-semibold text-base-content/80">Four steps per call.</span>
              The raw log is compacted into a turn list (caller · agent · tool call · tool result); the path is derived
              from the tool calls in code; the checks a string comparison can settle are settled without a model; only
              what is left is sent to Gemini 2.5 Flash on Vertex, together with the five paths and the rules below.
            </p>
            <p>
              <span class="font-semibold text-base-content/80">Quotes are mandatory.</span>
              Every finding must carry a verbatim quote, and the quote must come from the role it is attributed to — an
              “agent said” quote is checked against agent turns only. Findings whose quotes are not in the log
              letter-for-letter are discarded server-side. Repeats of one violation are collapsed into one entry.
            </p>
            <p>
              <span class="font-semibold text-base-content/80">Path first, then rules.</span>
              The path comes from the tool calls, not from the model. If the model reads the conversation as a different
              path, that disagreement is a finding in its own right (rule PATH). A call that was dropped before the
              phone step is marked <code>unfinished</code> and is not judged on its path at all.
            </p>
            <p>
              <span class="font-semibold text-base-content/80">Read it, do not trust it.</span>
              Open a verdict and the whole conversation is printed as the judge saw it, with every finding in the margin
              next to its own line. TTS pause tags are stripped for readability; “Open raw log” shows the untouched
              record.
            </p>
            <div class="pt-1"></div>
            ${judgeRules.map(
              (r: any) =>
                html`<div class="flex gap-2">
                  <span class="badge badge-sm font-mono shrink-0">${r.id}</span>
                  <span class="badge badge-ghost badge-sm shrink-0">${sevLabel(r.severity)}</span>
                  <span>${r.text}</span>
                </div>`,
            )}
            <div class="flex gap-2">
              <span class="badge badge-sm font-mono shrink-0">R6</span>
              <span class="badge badge-ghost badge-sm shrink-0">critical</span>
              <span>
                A ticket number that was created must be spoken to the caller before the call is ended. Decided by
                string comparison, never by the model.
              </span>
            </div>
            <div class="flex gap-2">
              <span class="badge badge-sm font-mono shrink-0">R8</span>
              <span class="badge badge-ghost badge-sm shrink-0">major</span>
              <span>
                A number read back to the caller must contain the digits that were sent to the tool. Compared by string,
                never by the model; a short form without the country code passes.
              </span>
            </div>
            <div class="flex gap-2">
              <span class="badge badge-sm font-mono shrink-0">PATH</span>
              <span class="badge badge-ghost badge-sm shrink-0">major</span>
              <span>The conversation does not read like the path the tool calls put this call on.</span>
            </div>
          </div>
        </details>
      </div>
    </div>
  `;

  // ════════════════════════════════════════════════════════════════════════
  // Tab bar
  // ════════════════════════════════════════════════════════════════════════
  const tabBar = html`
    <div role="tablist" class="tabs tabs-boxed mb-6 inline-flex w-fit">
      ${VISIBLE_TABS.map(
        ([id, label]) =>
          html`<button role="tab" class="tab ${activeTab === id ? 'tab-active' : ''}" @click=${() => setActiveTab(id)}>
            ${label}
          </button>`,
      )}
    </div>
  `;

  // ════════════════════════════════════════════════════════════════════════
  // System-Health tab
  // ════════════════════════════════════════════════════════════════════════
  const healthTab = html`
    <!-- System Health panel — checks API keys / services (read-only, fresh from .env) -->
    <div class="card bg-base-100 border border-base-300 shadow-sm">
      <div class="card-body p-5">
        <div class="flex items-center gap-2.5 flex-wrap">
          <span
            class="inline-block w-2.5 h-2.5 rounded-full ${!healthData
              ? 'bg-base-content/20'
              : healthData.overall === 'ok'
                ? 'bg-success ring-2 ring-success/20'
                : healthData.overall === 'warn'
                  ? 'bg-warning ring-2 ring-warning/20'
                  : 'bg-error ring-2 ring-error/20'}"
          ></span>
          <h3 class="font-semibold tracking-tight">System-Health</h3>
          ${healthData
            ? html`<span
                class="text-xs font-medium ${healthData.overall === 'ok'
                  ? 'text-success'
                  : healthData.overall === 'warn'
                    ? 'text-warning'
                    : 'text-error'}"
                >${healthData.overall === 'ok'
                  ? 'Alles OK'
                  : healthData.overall === 'warn'
                    ? 'Warnung'
                    : 'Probleme'}</span
              >`
            : nothing}
          ${healthData
            ? html`<span class="text-xs text-base-content/40"
                >· ${healthData.durationMs} ms · aus .env${healthData.cached ? ' · cached' : ''}</span
              >`
            : nothing}
          <button
            class="btn btn-sm btn-outline btn-primary ml-auto gap-2"
            @click=${() => runHealthCheck(true)}
            ?disabled=${healthLoading}
          >
            ${healthLoading
              ? html`<span class="loading loading-spinner loading-xs"></span>Prüfe…`
              : html`<svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    /></svg
                  >Aktualisieren`}
          </button>
        </div>
        ${healthError ? html`<p class="text-error text-sm mt-3">${healthError}</p>` : nothing}
        ${healthData
          ? html`<div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
              ${(healthData.services ?? []).map(
                (s: any) =>
                  html`<div
                    class="rounded-xl border p-4 transition-colors ${s.status === 'fail'
                      ? 'border-error/40 bg-error/5'
                      : s.status === 'warn'
                        ? 'border-warning/40 bg-warning/5'
                        : 'border-base-300 bg-base-200/40'}"
                  >
                    <div class="flex items-center gap-2">
                      <span
                        class="inline-block w-2 h-2 rounded-full shrink-0 ${s.status === 'ok'
                          ? 'bg-success ring-2 ring-success/20'
                          : s.status === 'warn'
                            ? 'bg-warning ring-2 ring-warning/20'
                            : 'bg-error ring-2 ring-error/20'}"
                      ></span>
                      <span class="font-medium text-sm flex-1 min-w-0 truncate">${s.label}</span>
                      <span class="text-[10px] text-base-content/40 tabular-nums shrink-0">${s.latencyMs} ms</span>
                    </div>
                    <div class="text-xs mt-2 ${s.ok ? 'text-base-content/70' : 'text-error font-medium'}">
                      ${s.ok ? s.info || 'OK' : s.error || 'Fehler'}
                    </div>
                    <div class="text-[11px] text-base-content/35 font-mono mt-2 tracking-tight">${s.keyMasked}</div>
                  </div>`,
              )}
            </div>`
          : html`<p class="text-xs text-base-content/50 mt-3">
              Prüft genau die Dienste, die in dieser Umgebung eingeschaltet sind — die Sonde folgt
              <code>STT_ENGINE</code>, <code>TTS_ENGINE</code> und <code>LLM_PROVIDER</code>, statt eine feste Liste zu
              prüfen. Dazu Twilio und den MCP-Tunnel. Die Keys werden frisch aus der <code>.env</code> gelesen.
            </p>`}

        <!-- Info disclosure: how the keys are loaded -->
        <details class="mt-4 border-t border-base-300 pt-3">
          <summary
            class="cursor-pointer select-none text-xs font-medium text-base-content/50 hover:text-base-content/80 transition-colors"
          >
            Wie werden die Keys geladen?
          </summary>
          <div class="text-xs text-base-content/60 leading-relaxed mt-3 space-y-2">
            <p>
              <span class="font-semibold text-base-content/80">Frisch aus der .env-Datei.</span>
              Bei jedem Klick auf <span class="font-medium">Aktualisieren</span> liest der Server die
              <code>.env</code>-Datei neu vom Datenträger (nicht den laufenden Prozess) und prüft genau diese Werte —
              Änderungen sind also sofort sichtbar.
            </p>
            <p>
              <span class="font-semibold text-base-content/80">Maskiert &amp; serverseitig.</span>
              Angezeigt wird nur eine maskierte Form (<code>erste4…letzte2</code>, z.&nbsp;B.
              <code>eff0…9e</code>). Der echte Key wird ausschließlich serverseitig für den API-Aufruf verwendet und
              verlässt nie den Browser.
            </p>
            <p>
              <span class="font-semibold text-base-content/80">Geprüfte Dienste:</span>
              Deepgram (STT), Cartesia (TTS), OpenRouter (LLM dev), Azure OpenAI (LLM prod), Twilio (Telefonie) und der
              MCP-Ticketing-Tunnel (<code>localhost:12345</code>).
            </p>
            <p class="text-warning/90 bg-warning/5 border border-warning/30 rounded-lg px-3 py-2">
              <span class="font-semibold">Hinweis:</span> Der Bot selbst liest die <code>.env</code> nur beim Start.
              Eine .env-Änderung wirkt sich auf laufende Anrufe erst nach einem
              <span class="font-semibold">Neustart</span> aus — die Health-Prüfung hier zeigt den neuen Wert jedoch
              sofort an.
            </p>
          </div>
        </details>
      </div>
    </div>
  `;

  // ════════════════════════════════════════════════════════════════════════
  // Quick Board tab
  // ════════════════════════════════════════════════════════════════════════
  const boardTab = html`
    <!-- Quick Board — shared pinned links + SOS tasks (backend) -->
    <div class="card bg-base-100 border border-base-300 shadow-sm">
      <div class="card-body p-5">
        <div class="flex items-center gap-2.5">
          <h3 class="font-semibold tracking-tight">Quick Board</h3>
          ${boardSaving ? html`<span class="loading loading-spinner loading-xs"></span>` : nothing}
          <span class="text-xs text-base-content/40 ml-auto">geteilt · alle Tester</span>
        </div>
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4">
          <!-- Pinned links -->
          <div>
            <h4 class="text-sm font-semibold mb-2 flex items-center gap-2">
              <span class="inline-block w-2 h-2 rounded-full bg-primary"></span>Wichtige Links
            </h4>
            <ul class="space-y-1.5 mb-3">
              ${boardLinks.length === 0
                ? html`<li class="text-xs text-base-content/40 italic">Noch keine Links.</li>`
                : boardLinks.map(
                    (l, i) =>
                      html`<li
                        class="flex items-center gap-2 rounded-lg border border-base-300 bg-base-200/40 px-3 py-2"
                      >
                        <a
                          href="${l.url}"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="text-sm text-primary hover:underline truncate flex-1"
                          title="${l.url}"
                          >${l.title || l.url}</a
                        >
                        <button class="btn btn-ghost btn-xs text-error" @click=${() => removeLink(i)} title="Entfernen">
                          ✕
                        </button>
                      </li>`,
                  )}
            </ul>
            <div class="flex flex-col sm:flex-row gap-2">
              <input
                class="input input-bordered input-sm flex-1"
                placeholder="Titel (z.B. ClickUp)"
                .value=${newLinkTitle}
                @input=${(e: Event) => setNewLinkTitle((e.target as HTMLInputElement).value)}
              />
              <input
                class="input input-bordered input-sm flex-1"
                placeholder="https://…"
                .value=${newLinkUrl}
                @input=${(e: Event) => setNewLinkUrl((e.target as HTMLInputElement).value)}
                @keydown=${(e: KeyboardEvent) => {
                  if (e.key === 'Enter') addLink();
                }}
              />
              <button class="btn btn-primary btn-sm" @click=${addLink} ?disabled=${!newLinkUrl.trim()}>+ Link</button>
            </div>
          </div>
          <!-- SOS tasks -->
          <div>
            <h4 class="text-sm font-semibold mb-2 flex items-center gap-2">
              <span class="inline-block w-2 h-2 rounded-full bg-error"></span>SOS-Tasks
            </h4>
            <ul class="space-y-1.5 mb-3">
              ${boardTasks.length === 0
                ? html`<li class="text-xs text-base-content/40 italic">Keine offenen Tasks.</li>`
                : boardTasks.map(
                    t =>
                      html`<li
                        class="flex items-center gap-2 rounded-lg border border-base-300 bg-base-200/40 px-3 py-2"
                      >
                        <input
                          type="checkbox"
                          class="checkbox checkbox-sm"
                          ?checked=${t.done}
                          @change=${() => toggleTask(t.id)}
                        />
                        <span class="text-sm flex-1 ${t.done ? 'line-through opacity-50' : ''}">${t.text}</span>
                        <button
                          class="btn btn-ghost btn-xs text-error"
                          @click=${() => removeTask(t.id)}
                          title="Löschen"
                        >
                          ✕
                        </button>
                      </li>`,
                  )}
            </ul>
            <div class="flex gap-2">
              <input
                class="input input-bordered input-sm flex-1"
                placeholder="Neuer SOS-Task…"
                .value=${newTaskText}
                @input=${(e: Event) => setNewTaskText((e.target as HTMLInputElement).value)}
                @keydown=${(e: KeyboardEvent) => {
                  if (e.key === 'Enter') addTask();
                }}
              />
              <button class="btn btn-primary btn-sm" @click=${addTask} ?disabled=${!newTaskText.trim()}>+ Task</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  `;

  // ════════════════════════════════════════════════════════════════════════
  // Kosten tab — Cost Analytics table + editable price rates
  // ════════════════════════════════════════════════════════════════════════
  const preiseCard = html`
    <!-- Preise — UI-editable cost rates + per-minute view -->
    <div class="card bg-base-100 border border-base-300 shadow-sm">
      <div class="card-body p-5">
        <div class="flex items-center gap-2.5">
          <h3 class="font-semibold tracking-tight">Preise</h3>
          ${pricingSaving ? html`<span class="loading loading-spinner loading-xs"></span>` : nothing}
          ${pricingSaved ? html`<span class="text-xs text-success font-medium">✓ Gespeichert</span>` : nothing}
          <span class="text-xs text-base-content/40 ml-auto">geteilt · Kostenraten (USD)</span>
        </div>
        <p class="text-xs text-base-content/50 mt-1">
          🟢 live = vom Anbieter automatisch (OpenRouter/Twilio) · 🔵 hier editierbar (Cartesia/Azure). Der LLM-Preis
          fließt sofort in die Kostenberechnung; die volle Aufschlüsselung folgt in Phase 2.
        </p>
        ${[
          {
            t: 'LLM (OpenRouter · dev)',
            dot: 'bg-primary',
            f: [
              ['orIn', 'Input $/1M Tokens'],
              ['orOut', 'Output $/1M Tokens'],
            ],
          },
          {
            t: 'LLM (Azure · prod)',
            dot: 'bg-secondary',
            f: [
              ['azIn', 'Input $/1M Tokens'],
              ['azOut', 'Output $/1M Tokens'],
            ],
          },
          {
            t: 'Sprache & Telefonie',
            dot: 'bg-accent',
            f: [
              ['cartesia', 'Cartesia $/1M Zeichen'],
              ['deepgram', 'Deepgram $/Min'],
              ['twilio', 'Twilio $/Min'],
              ['usdToEur', 'USD → EUR'],
            ],
          },
        ].map(
          g => html`
            <h4 class="text-sm font-semibold mb-2 mt-4 flex items-center gap-2">
              <span class="inline-block w-2 h-2 rounded-full ${g.dot}"></span>${g.t}
            </h4>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
              ${g.f.map(
                ([k, lbl]) => html`
                  <label class="form-control">
                    <span class="text-xs text-base-content/60 mb-1 block">${lbl}</span>
                    <input
                      type="number"
                      step="any"
                      min="0"
                      class="input input-bordered input-sm w-full"
                      .value=${String(pricing[k] ?? 0)}
                      @input=${(e: Event) => setPrice(k, (e.target as HTMLInputElement).value)}
                    />
                  </label>
                `,
              )}
            </div>
          `,
        )}
        ${(() => {
          const e = eurPerMin();
          return html`
            <div class="rounded-xl border border-base-300 bg-base-200/40 p-3 mt-4">
              <div class="text-sm font-semibold mb-2">Kosten pro Minute (Schätzung)</div>
              <div class="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                <div>LLM: <span class="font-mono">${eur(e.llm)}</span></div>
                <div>Cartesia: <span class="font-mono">${eur(e.cartesia)}</span></div>
                <div>Deepgram: <span class="font-mono">${eur(e.deepgram)}</span></div>
                <div>Twilio: <span class="font-mono">${eur(e.twilio)}</span></div>
              </div>
              <div class="text-sm mt-2">
                Σ <span class="font-mono font-semibold">${eur(e.total)}/Min</span> · typischer 3-Min-Anruf ≈
                <span class="font-mono">${eur(e.total * 3)}</span>
              </div>
            </div>
          `;
        })()}
        <div class="flex items-center gap-3 mt-4">
          <button class="btn btn-primary btn-sm" @click=${savePricing} ?disabled=${pricingSaving}>Speichern</button>
          <button
            class="btn btn-ghost btn-sm"
            @click=${() => setPricing({ ...PRICING_DEFAULT })}
            ?disabled=${pricingSaving}
          >
            Standard
          </button>
          ${pricingMeta
            ? html`<span class="text-xs text-base-content/40 ml-auto"
                >zuletzt:
                ${pricingMeta.by}${pricingMeta.at ? ` · ${new Date(pricingMeta.at).toLocaleString('de-DE')}` : ''}</span
              >`
            : nothing}
        </div>
        ${pricingError ? html`<p class="text-error text-sm mt-2">${pricingError}</p>` : nothing}
      </div>
    </div>
  `;

  // Thunk so the embedded CostAnalytics() stateful component only mounts when
  // the Kosten tab is active (matches LocalTicketOverview's conditional pattern).
  const kostenTab = () => html`
    <div class="flex flex-col gap-6">
      ${preiseCard}
      <p class="text-sm text-base-content/70">
        Kosten pro Anruf — Prognose auf Basis der oben editierbaren Preise. Gilt für OpenRouter (dev) und Azure (prod);
        die jeweils aktive Engine bestimmt den LLM-Tarif.
      </p>
      <!-- Cost Analytics table (all calls incl. test calls) -->
      <div class="-mx-4 -my-4">${CostAnalytics()}</div>
    </div>
  `;

  // ════════════════════════════════════════════════════════════════════════
  // Tests tab — profile selector, call controls, verdicts, logs, history
  // ════════════════════════════════════════════════════════════════════════
  const testsTab = html`
    ${callers.length === 0
      ? html`<div class="alert alert-warning mb-4">
          Keine Test-Anrufer geladen. (Test-Lab-Berechtigung fehlt — setze
          <code>TEST_LAB_ADMIN_USER_IDS</code>.)
        </div>`
      : nothing}

    <!-- Profile + Scenario + Language selector -->
    <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
      <div>
        <label class="text-sm font-semibold block mb-1">Profil (Identität):</label>
        <select
          class="select select-bordered w-full"
          .value=${selectedProfileId}
          @change=${(e: Event) => {
            setSelectedProfileId((e.target as HTMLSelectElement).value);
            setVerdicts([]);
            setActiveSessionID(null);
          }}
          ?disabled=${callState === 'connecting' || callState === 'connected'}
        >
          ${callers.map(c => html`<option value="${c.profileId}">${c.label}</option>`)}
        </select>
      </div>
      <div>
        <label class="text-sm font-semibold block mb-1">Test-Schritt:</label>
        <select
          class="select select-bordered w-full"
          .value=${selectedScenario}
          @change=${(e: Event) => setSelectedScenario((e.target as HTMLSelectElement).value)}
          ?disabled=${callState === 'connecting' || callState === 'connected'}
        >
          ${scenarios.map(
            s => html`<option value="${s.id}" ?selected=${s.id === selectedScenario}>${s.label}</option>`,
          )}
        </select>
      </div>
      <div>
        <label class="text-sm font-semibold block mb-1">Sprache:</label>
        <select
          class="select select-bordered w-full"
          .value=${selectedLang}
          @change=${(e: Event) => setSelectedLang((e.target as HTMLSelectElement).value as 'de' | 'en')}
          ?disabled=${callState === 'connecting' || callState === 'connected'}
        >
          <option value="de" ?selected=${selectedLang === 'de'}>Deutsch</option>
          <option value="en" ?selected=${selectedLang === 'en'}>English</option>
        </select>
      </div>
    </div>

    <!-- Scenario description hint -->
    ${selectedScenarioInfo
      ? html`<p class="text-xs opacity-70 mb-3 italic">
          <strong>${selectedScenarioInfo.label}:</strong> ${selectedScenarioInfo.description}
        </p>`
      : nothing}

    <!-- Credentials info card -->
    ${selectedProfile
      ? html`
          <div class="card bg-base-200 mb-4">
            <div class="card-body p-4">
              <div class="flex justify-between items-start mb-2">
                <h3 class="font-semibold">${selectedProfile.label} ${flowBadge(selectedProfile.expectedFlow)}</h3>
              </div>
              <p class="text-sm opacity-80 mb-3">${selectedProfile.description}</p>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm font-mono">
                <div><span class="opacity-60">Vorname:</span> ${selectedProfile.credentials.firstName}</div>
                <div><span class="opacity-60">Nachname:</span> ${selectedProfile.credentials.lastName}</div>
                <div><span class="opacity-60">E-Mail:</span> ${selectedProfile.credentials.email}</div>
                <div><span class="opacity-60">Telefon:</span> ${selectedProfile.credentials.phone}</div>
                <div><span class="opacity-60">Konto:</span> ${selectedProfile.credentials.accountNumber}</div>
                <div>
                  <span class="opacity-60">Dossier(s):</span>
                  ${selectedProfile.credentials.dossierNumbers.join(', ')}
                </div>
                ${selectedProfile.credentials.ticketNumber
                  ? html`<div class="col-span-2">
                      <span class="opacity-60">Ticket:</span>
                      <code>${selectedProfile.credentials.ticketNumber}</code>
                    </div>`
                  : nothing}
              </div>
              <div class="alert alert-info mt-3 text-xs">
                <strong>Hinweis:</strong> Lies diese Werte dem Bot vor, wenn er fragt. Der Bot wiederholt sie zur
                Bestätigung (Spell-back) — bestätige mit „ja" / „yes".
              </div>
            </div>
          </div>
        `
      : nothing}

    <!-- Start/Stop button -->
    <div class="flex flex-col items-center gap-3 mb-6">
      <button
        class="${callButtonClass}"
        ?disabled=${callState === 'connecting' || !selectedProfileId}
        @click=${handleCallButtonClick}
      >
        <img src="${callIcon}" alt="" class="w-5 h-5" draggable="false" />
        ${callButtonLabel}
      </button>
      <p class="text-sm text-base-content/60">
        Status: <span class="font-semibold">${callState}</span>
        ${activeSessionID ? html` · <span class="font-mono text-xs">${activeSessionID}</span>` : nothing}
      </p>
    </div>

    <!-- Error display -->
    ${errorMsg ? html`<div class="alert alert-error mb-4"><strong>Fehler:</strong> ${errorMsg}</div>` : nothing}

    <!-- Final summary banner — shown after the call ends -->
    ${activeSessionID && callState === 'stopped' && verdicts.length > 0
      ? (() => {
          const passed = verdicts.filter(v => v.passed).length;
          const failed = verdicts.filter(v => !v.passed).length;
          const allPassed = failed === 0 && passed > 0;
          return html`
            <div class="alert ${allPassed ? 'alert-success' : 'alert-error'} mb-4">
              <div class="flex flex-col items-start">
                <strong>
                  ${allPassed
                    ? '✅ Test bestanden'
                    : `❌ Test fehlgeschlagen (${passed}/${verdicts.length} verdicts bestanden)`}
                </strong>
                <ul class="mt-2 text-sm space-y-1">
                  ${verdicts.map(
                    v => html`
                      <li>
                        ${v.passed ? '✅' : '❌'}
                        <code class="font-mono">${v.check}</code>
                        ${v.reason ? html` — <em class="opacity-80">${v.reason}</em>` : nothing}
                      </li>
                    `,
                  )}
                </ul>
              </div>
            </div>
          `;
        })()
      : nothing}

    <!-- Per-call cost summary — shown after the call ends -->
    ${lastCallSession && (callState === 'stopped' || callState === 'error')
      ? html`
          <div class="card bg-base-200 mb-6 border border-secondary/30">
            <div class="card-body p-4">
              <h3 class="font-semibold mb-1 flex items-center gap-2">
                Kosten dieses Anrufs ${costMethodBadge(lastCallSession.estimationMethod)}
              </h3>
              <p class="text-xs opacity-70 mb-3">
                Geschätzt (char-Heuristik) auf Basis der aktuellen Preise — gilt für OpenRouter (dev) und Azure (prod).
                Genaue Per-Turn-Tokens folgen in Phase 2.
              </p>
              <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 text-sm mb-3">
                <div>
                  <div class="text-xs opacity-60">LLM</div>
                  <div class="font-mono">${eur(lastCallSession.totalCostEUR)}</div>
                </div>
                <div>
                  <div class="text-xs opacity-60">Twilio</div>
                  <div class="font-mono">${eur(lastCallSession.costTwilioEUR)}</div>
                </div>
                <div>
                  <div class="text-xs opacity-60">Deepgram</div>
                  <div class="font-mono">${eur(lastCallSession.costDeepgramEUR)}</div>
                </div>
                <div>
                  <div class="text-xs opacity-60">Cartesia</div>
                  <div class="font-mono">${eur(lastCallSession.costCartesiaEUR)}</div>
                </div>
                <div>
                  <div class="text-xs opacity-60">Gesamt (inkl. Sprache)</div>
                  <div class="font-mono font-semibold text-lg text-primary">
                    ${eur(lastCallSession.costTotalEUR ?? lastCallSession.totalCostEUR)}
                  </div>
                </div>
              </div>
              <div class="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs opacity-80">
                <div>Turns: <span class="font-mono">${lastCallSession.totalTurns ?? '—'}</span></div>
                <div>
                  Tokens (in/out):
                  <span class="font-mono"
                    >${(lastCallSession.totalInputTokens ?? 0).toLocaleString()} /
                    ${(lastCallSession.totalOutputTokens ?? 0).toLocaleString()}</span
                  >
                </div>
                <div>
                  Gespr. Zeichen:
                  <span class="font-mono">${(lastCallSession.spokenChars ?? 0).toLocaleString()}</span>
                </div>
                <div>
                  Dauer:
                  <span class="font-mono"
                    >${typeof lastCallSession.durationMs === 'number'
                      ? `${(lastCallSession.durationMs / 1000).toFixed(1)} s`
                      : '—'}</span
                  >
                </div>
              </div>
            </div>
          </div>
        `
      : nothing}

    <!-- Feedback remains available only after the call ends. -->
    ${activeSessionID && (callState === 'stopped' || callState === 'error')
      ? html`<div class="mb-6" style="max-width: 1040px; margin-left: auto; margin-right: auto;">
          ${CallFeedbackPanel({ callID: activeSessionID })}
        </div>`
      : nothing}

    <!-- Live verdict stream -->
    ${activeSessionID
      ? html`
          <div class="card bg-base-200 mb-6">
            <div class="card-body p-4">
              <h3 class="font-semibold mb-2">Live Verdicts</h3>
              ${verdicts.length === 0
                ? html`<p class="text-sm italic opacity-60">Wartet auf erste Tool-Ausführung…</p>`
                : html`<ul class="space-y-2">
                    ${verdicts.map(
                      v => html`
                        <li class="flex items-start gap-2 p-2 bg-base-100 rounded">
                          ${verdictBadge(v)}
                          <div class="flex-1">
                            <span class="font-mono text-sm">${v.check}</span>
                            ${v.reason ? html`<span class="text-xs opacity-70 ml-2">${v.reason}</span>` : nothing}
                          </div>
                          <span class="text-xs opacity-50 font-mono">
                            ${new Date(v.ts).toLocaleTimeString('de-DE')}
                          </span>
                        </li>
                      `,
                    )}
                  </ul>`}
            </div>
          </div>
        `
      : nothing}

    <!-- Live call logs -->
    ${activeSessionID
      ? html`
          <div class="mb-6">
            <h3 class="text-lg font-semibold mb-2">Live Anrufprotokoll</h3>
            <div class="flex items-center mb-2 gap-2">
              <label class="text-sm">Filter:</label>
              <select
                class="select select-sm select-bordered"
                @change=${(e: Event) => setLogTypeFilter((e.target as HTMLSelectElement).value)}
              >
                <option value="all" ?selected=${logTypeFilter === 'all'}>alle</option>
                ${callLogEventTypes.map(
                  eventType =>
                    html`<option value="${eventType}" ?selected=${logTypeFilter === eventType}>${eventType}</option>`,
                )}
              </select>
            </div>
            <div class="bg-base-200 rounded-lg p-4 min-h-64 max-h-[50vh] overflow-y-auto">
              ${renderCallLogList(
                ((callLogSub.value as any[]) ?? []).filter(
                  (log: any) => logTypeFilter === 'all' || log.type === logTypeFilter,
                ),
              )}
            </div>
          </div>
        `
      : nothing}

    <!-- History -->
    <div class="mt-4">
      <h3 class="text-lg font-semibold mb-3">Verlauf (letzte 20)</h3>
      ${historyQuery.loading
        ? html`<p class="italic opacity-60">Lade…</p>`
        : (historyQuery.value as CallSession[] | undefined)?.length
          ? html`
              <div class="overflow-x-auto">
                <table class="table table-sm">
                  <thead>
                    <tr>
                      <th>Zeit</th>
                      <th>Profil</th>
                      <th>Status</th>
                      <th>Verdicts</th>
                      <th>Kosten</th>
                      <th>Session ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${(historyQuery.value as CallSession[]).map(s => {
                      let verdictSummary = '—';
                      try {
                        const parsed = JSON.parse(s.testVerdictsJSON ?? '[]') as VerdictUI[];
                        const passed = parsed.filter(v => v.passed).length;
                        verdictSummary = `${passed}/${parsed.length}`;
                      } catch {
                        /* leave dash */
                      }
                      return html`
                        <tr>
                          <td class="font-mono text-xs">${formatDate(s.endedAt ?? s.startedAt)}</td>
                          <td>${s.testCallerProfile ?? '—'}</td>
                          <td>
                            <span
                              class="badge ${s.outcome === 'ticket-created'
                                ? 'badge-success'
                                : s.outcome === 'incomplete'
                                  ? 'badge-warning'
                                  : 'badge-ghost'}"
                            >
                              ${s.outcome ?? '—'}
                            </span>
                          </td>
                          <td class="font-mono">${verdictSummary}</td>
                          <td class="font-mono text-xs">
                            ${typeof s.totalCostEUR === 'number' && s.totalCostEUR > 0 ? eur(s.totalCostEUR) : '—'}
                          </td>
                          <td class="font-mono text-xs opacity-60">${s.sessionID}</td>
                        </tr>
                      `;
                    })}
                  </tbody>
                </table>
              </div>
            `
          : html`<p class="italic opacity-60">Noch keine Test-Anrufe.</p>`}
    </div>
  `;

  return html`
    <section class="w-full py-8 px-4">
      <div class="card bg-base-100 mx-auto" tabindex="0" style="max-width: 1200px;">
        <div class="card-body p-8">
          <button class="btn btn-ghost mb-4 self-start flex items-center gap-1" @click=${() => goTo('/')}>
            <img src="${arrowLeftIcon}" alt="Zurück" class="inline w-5 h-5 mr-1" draggable="false" />
            Zurück
          </button>

          <h2 class="card-title text-2xl font-bold text-primary mb-2">Settings</h2>
          <p class="text-base-content/70 mb-6">Zielnummer für Weiterleitungen verwalten und den Systemstatus prüfen.</p>

          ${tabBar} ${activeTab === 'destination' ? CallForwardingPanel() : nothing}
          ${activeTab === 'telephone-flow' ? TelephoneFlowPanel() : nothing}
          ${activeTab === 'tests' ? testsTab : nothing} ${activeTab === 'kosten' ? kostenTab() : nothing}
          ${activeTab === 'health' ? healthTab : nothing} ${activeTab === 'board' ? boardTab : nothing}
          ${activeTab === 'judge' ? renderJudgeTab() : nothing}
          ${activeTab === 'ndi-voice' ? AutomatedSuitePage() : nothing}
        </div>
      </div>
    </section>
  `;
});
